import React, { useState } from "react";
import ConsultationForm from "./Consultation";
import ReceptionLobby1 from "../assets/images/Reception/ReceptionLobby-1.png";
import ReceptionLobby2 from "../assets/images/Reception/ReceptionLobby-2.png";
import ReceptionLobby3 from "../assets/images/Reception/ReceptionLobby-3.png";
import ReceptionLobby4 from "../assets/images/Reception/ReceptionLobby-4.png";
import ReceptionLobby5 from "../assets/images/Reception/ReceptionLobby-5.png";
import ReceptionLobby6 from "../assets/images/Reception/ReceptionLobby-6.png";
import ReceptionLobby7 from "../assets/images/Reception/ReceptionLobby-7.png";
import ReceptionLobby8 from "../assets/images/Reception/ReceptionLobby-8.png";
import ReceptionLobby9 from "../assets/images/Reception/ReceptionLobby-9.png";
import SEO from "../components/SEO";


/**
 * Reception Lobby Designs — landing page
 * Same design system as OTSuiteDesigns.jsx — black/white/grey/ash palette,
 * spec-line cards (Zone / Size / Seating), 2D/3D image toggle,
 * "Get Free Quote" opens the shared Consultation modal.
 */

const FONT_IMPORT_URL =
  "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";

const tokens = {
  ink: "#343F86",
  bg: "#ECEEF6",
  card: "#FFFFFF",
  teal: "#3A4060",
  tealDark: "#262E63",
  amber: "#8A8F9C",
  blue: "#5B6072",
  hairline: "#CDD1E0",
  sub: "#5B6072",
};

const lobbies = [
  {
    name: "Grand Atrium Reception",
    zone: "Ground Floor",
    size: "120 sq.m",
    seating: "40+ Seats",
    tag: "Atrium",
    note: "Double-height ceiling, central help desk, natural light wells, water feature accent wall.",
    image: ReceptionLobby1, // 👈 add your image path/URL here
  },
  {
    name: "Compact OPD Reception Counter",
    zone: "Ground Floor",
    size: "30 sq.m",
    seating: "12 Seats",
    tag: "Compact",
    note: "Single-counter check-in, digital token display, wheelchair-accessible queueing.",
    image: ReceptionLobby2, // 👈 add your image path/URL here
  },
  {
    name: "Multi-Counter Admissions Lobby",
    zone: "Ground Floor",
    size: "80 sq.m",
    seating: "30 Seats",
    tag: "Admissions",
    note: "Three dedicated counters, separate billing desk, document-scanning kiosk integration.",
    image: ReceptionLobby3, // 👈 add your image path/URL here
  },
  {
    name: "Pediatric-Friendly Reception",
    zone: "Ground Floor",
    size: "45 sq.m",
    seating: "18 Seats",
    tag: "Pediatric",
    note: "Themed play corner, low-height counter for children, soft rounded furniture.",
    image: ReceptionLobby4, // 👈 add your image path/URL here
  },
  {
    name: "Premium / VIP Patient Lounge",
    zone: "Ground Floor",
    size: "55 sq.m",
    seating: "16 Seats",
    tag: "Premium",
    note: "Private check-in pods, complimentary beverage counter, plush seating, dedicated valet desk.",
    image: ReceptionLobby5, // 👈 add your image path/URL here
  },
  {
    name: "Diagnostic Centre Waiting Lounge",
    zone: "Ground Floor",
    size: "60 sq.m",
    seating: "25 Seats",
    tag: "Diagnostics",
    note: "Digital queue display synced to lab/imaging units, charging points at every seat.",
    image: ReceptionLobby6, // 👈 add your image path/URL here
  },
  {
    name: "Emergency Triage Reception",
    zone: "Ground Floor",
    size: "35 sq.m",
    seating: "10 Seats",
    tag: "Emergency",
    note: "Direct ambulance-bay sightline, rapid triage counter, separate isolation waiting bay.",
    image: ReceptionLobby7, // 👈 add your image path/URL here
  },
  {
    name: "Corporate Wellness Centre Lobby",
    zone: "Ground Floor",
    size: "50 sq.m",
    seating: "20 Seats",
    tag: "Wellness",
    note: "Branded check-in wall, self-service kiosk, lounge-style seating with charging tables.",
    image: ReceptionLobby8, // 👈 add your image path/URL here
  },
  {
    name: "Multi-Specialty Hospital Main Lobby",
    zone: "Ground Floor",
    size: "150 sq.m",
    seating: "50+ Seats",
    tag: "Main Lobby",
    note: "Wayfinding signage hub, information desk, cafe corner, central elevator bank visibility.",
    image: ReceptionLobby9, // 👈 add your image path/URL here
  },
];

function SpecLine({ zone, size, seating }) {
  return (
    <div
      style={{
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        fontSize: 11.5,
        letterSpacing: 0.3,
        color: tokens.sub,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span>ZONE&nbsp;<b style={{ color: tokens.teal }}>{zone}</b></span>
      <span>·</span>
      <span>SIZE&nbsp;<b style={{ color: tokens.ink }}>{size}</b></span>
      <span>·</span>
      <span>{seating}</span>
    </div>
  );
}

function LobbyCard({ l, onGetQuote }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: tokens.card,
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${tokens.hairline}`,
        boxShadow: hover
          ? "0 14px 28px -14px rgba(14,124,123,0.35)"
          : "0 2px 6px -2px rgba(43,58,66,0.08)",
        transform: hover ? "translateY(-3px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      {/* corner bracket signature */}
      <div
        style={{
          position: "absolute", top: 10, left: 10, width: 22, height: 22,
          borderTop: `2px solid ${tokens.teal}`, borderLeft: `2px solid ${tokens.teal}`,
          zIndex: 2,
        }}
      />
      <div style={{ height: 168, position: "relative", background: tokens.tealDark }}>
        {l.image ? (
          <img
            src={l.image}
            alt={l.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            style={{
              width: "100%", height: "100%", display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "rgba(255,255,255,0.55)",
              fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 12, letterSpacing: 0.5,
            }}
          >
            ADD IMAGE
          </div>
        )}
        {/* tag chip */}
        <span
          style={{
            position: "absolute", bottom: 10, right: 12,
            fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 11,
            color: "#fff", background: "rgba(0,0,0,0.45)",
            padding: "2px 8px", borderRadius: 2,
          }}
        >
          {l.tag.toUpperCase()}
        </span>
      </div>
      <div style={{ padding: "16px 16px 18px" }}>
        <h3
          style={{
            fontFamily: "'Montserrat', 'Inter', sans-serif", fontWeight: 600, fontSize: 17.5,
            color: tokens.ink, margin: "0 0 6px", lineHeight: 1.25,
          }}
        >
          {l.name}
        </h3>
        <p style={{ fontSize: 13, color: tokens.sub, margin: "0 0 10px", lineHeight: 1.45 }}>
          {l.note}
        </p>
        <SpecLine zone={l.zone} size={l.size} seating={l.seating} />
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => onGetQuote(l)}
            style={{
              flex: 1, padding: "9px 10px", fontSize: 12.5, fontWeight: 600,
              fontFamily: "'Montserrat', 'Inter', sans-serif", letterSpacing: 0.2,
              background: tokens.amber, color: tokens.ink,
              border: "none", borderRadius: 3, cursor: "pointer",
            }}
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ReceptionLobbyDesigns() {
  const [showConsultation, setShowConsultation] = useState(false);
  const [selectedLobby, setSelectedLobby] = useState(null);

  const handleGetQuote = (lobby) => {
    setSelectedLobby(lobby);
    setShowConsultation(true);
  };

  return (
     <>
    <SEO/>
    <div
      style={{
        background: tokens.bg,
        color: tokens.ink,
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        minHeight: "100vh",
      }}
    >
      <style>{`@import url('${FONT_IMPORT_URL}');
        * { box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
        button:focus-visible, a:focus-visible { outline: 2px solid ${tokens.teal}; outline-offset: 2px; }
        .lobby-grid-3x3 { grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 900px) { .lobby-grid-3x3 { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .lobby-grid-3x3 { grid-template-columns: 1fr; } }
        .lobby-modal-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(5px);
          display: flex; align-items: center; justify-content: center;
          padding: 1rem;
        }
        .lobby-modal-inner {
          position: relative; width: 100%; max-width: 680px;
          max-height: 90vh; overflow-y: auto; border-radius: 18px;
        }
        .lobby-modal-close {
          position: absolute; top: 14px; right: 16px; z-index: 10;
          background: #262E63; border: none; color: #fff;
          width: 32px; height: 32px; border-radius: 50%;
          font-size: 20px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          line-height: 1;
        }
      `}</style>

      {/* spacer so the fixed/sticky page navbar in the host app doesn't overlap the grid */}
      <div style={{ height: 32 }} />

      {/* GRID */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 24px 10px" }}>
        <div
          className="lobby-grid-3x3"
          style={{
            display: "grid",
            gap: 22,
            marginTop: 70,
          }}
        >
          {lobbies.map((l, i) => (
            <LobbyCard key={i} l={l} onGetQuote={handleGetQuote} />
          ))}
        </div>
      </section>

      {/* Consultation Modal — opens on "Get Free Quote" click */}
      {showConsultation && (
        <div
          className="lobby-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowConsultation(false); }}
        >
          <div className="lobby-modal-inner">
            <button
              className="lobby-modal-close"
              onClick={() => setShowConsultation(false)}
              aria-label="Close consultation form"
            >
              ×
            </button>
            <ConsultationForm suite={selectedLobby} />
          </div>
        </div>
      )}
    </div>
    </>
  );
}

